/* ============================================================
   The viewport every isometric scene is seen through.

   One orthographic camera at one angle, so the hero, the aisle and the
   level diagrams read as views of a single floor rather than five
   unrelated drawings.

   The light is an environment, not lamps. A small equirectangular
   gradient — bright overhead, turquoise off one shoulder, blue off the
   other, near-black below — is convolved into an IBL probe and lights
   every block from every direction at once. That is what puts a value
   grade across each face instead of a flat fill, and it is the same
   claim the stylesheet makes about the mark: the brand is the light
   falling on things, not the paint on them.

   Nothing here animates. A scene is drawn once when it is built, once
   more per resize, and — the single exception — for the second or so
   it takes to rise into place the first time a reader scrolls to it.
   There is no render loop at all: five of these sit on the landing
   page, and five idle rAF loops is a warm phone for no reason.
   ============================================================ */

import gsap from 'gsap';
import * as THREE from 'three';

export type Tick = (t: number) => void;

export interface Stage {
  root: THREE.Group;
  scene: THREE.Scene;
  render: () => void;
  /** Registers per-frame work. The loop only runs while the canvas is on
   *  screen, and never at all under reduced motion — which instead gets the
   *  frame the drift would have settled on a minute in. */
  onTick: (fn: Tick) => void;
  /** Frames what has been built. 1 is a tight fit; below 1 the floor
   *  deliberately runs off the edges, which is what a bleed wants.
   *
   *  `target` narrows the measurement to part of the scene — a floor slab
   *  sized to never show an edge would otherwise drag the frame out to
   *  include the very edge it exists to hide. */
  fit: (pad?: number, offset?: [number, number], target?: THREE.Object3D) => void;
  dispose: () => void;
}

export interface StageOptions {
  still?: boolean;
}

/* ─────────────────────────────  THE LIGHT  ───────────────────────────── */

const blob = (cx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) => {
  const g = cx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, 'transparent');
  cx.fillStyle = g;
  cx.fillRect(x - r, y - r, r * 2, r * 2);
};

/** The room, as one 512×256 equirectangular strip: y is the vertical
 *  sweep from zenith to nadir, x is the turn around the horizon. */
function room() {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 256;
  const cx = canvas.getContext('2d')!;

  const sky = cx.createLinearGradient(0, 0, 0, 256);
  sky.addColorStop(0, '#eaf4ff');      // zenith — the key
  sky.addColorStop(0.3, '#93a9cb');
  sky.addColorStop(0.48, '#26314a');
  sky.addColorStop(0.56, '#0d1322');   // horizon
  sky.addColorStop(1, '#05070d');      // the floor of the room
  cx.fillStyle = sky;
  cx.fillRect(0, 0, 512, 256);

  // The two brand lights, placed to match the radial washes behind the page.
  blob(cx, 110, 118, 145, 'rgba(94, 234, 212, .5)');
  blob(cx, 372, 132, 165, 'rgba(59, 130, 246, .46)');
  blob(cx, 250, 26, 120, 'rgba(255, 255, 255, .85)');
  blob(cx, 30, 40, 90, 'rgba(160, 210, 255, .5)');

  const texture = new THREE.CanvasTexture(canvas);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// The pixels are shared; the probe convolved from them is not — a PMREM
// render target belongs to the renderer that made it, one per canvas.
let sky: THREE.Texture | null = null;

/* ─────────────────────────────  THE STAGE  ───────────────────────────── */

// ponytail: one WebGL context per scene, five on the landing page — inside
// the browser's cap but not free. If it ever needs to be one, the seam is a
// single renderer with a scissor rect per host element.
export function createStage(host: HTMLElement, options: StageOptions = {}): Stage | null {
  const { still = false } = options;

  let renderer: THREE.WebGLRenderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch {
    host.dataset.webgl = 'unavailable';
    return null;
  }

  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.setAttribute('aria-hidden', 'true');
  host.append(renderer.domElement);

  const scene = new THREE.Scene();
  const root = new THREE.Group();
  scene.add(root);

  sky ??= room();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const probe = pmrem.fromEquirectangular(sky);
  scene.environment = probe.texture;
  pmrem.dispose();

  // One hard key on top of the probe, only so the blocks cast. Everything
  // else about their shading comes from the environment.
  const key = new THREE.DirectionalLight(0xdceaff, 1.15);
  key.position.set(26, 46, 16);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.bias = -0.0012;
  key.shadow.normalBias = 0.03;
  key.shadow.camera.near = 1;
  key.shadow.camera.far = 140;
  scene.add(key);

  const camera = new THREE.OrthographicCamera(-9, 9, 4.5, -4.5, 0.1, 200);
  camera.position.set(26, 21, 26);
  camera.lookAt(0, 0.6, 0);

  const render = () => renderer.render(scene, camera);

  // What the frustum has to contain, in camera space. Measured from the
  // built floor rather than guessed per scene, so no composition can be
  // cropped by a hand-tuned number that quietly stopped matching it.
  let view = { x: 0, y: 0, w: 18, h: 9 };
  // Held apart from `view` because it is not a property of the content: a
  // composition pushed to one side of a wide frame has nowhere to go on a
  // phone, where the same offset just shoves it off the edge.
  let bias: [number, number] = [0, 0];
  const corner = new THREE.Vector3();

  const resize = () => {
    const { width, height } = host.getBoundingClientRect();
    if (!width || !height) return;
    const aspect = width / height;
    const w = Math.max(view.w, view.h * aspect);
    const h = w / aspect;
    // Full offset on a wide frame, none on a square or tall one.
    const lean = Math.min(1, Math.max(0, (aspect - 0.95) / 0.75));
    const x = view.x + bias[0] * lean;
    const y = view.y + bias[1];
    camera.left = x - w / 2;
    camera.right = x + w / 2;
    camera.top = y + h / 2;
    camera.bottom = y - h / 2;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);
    render();
  };

  const fit: Stage['fit'] = (pad = 1, offset = [0, 0], target = root) => {
    camera.updateMatrixWorld();
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    target.updateMatrixWorld(true);

    // Measured block by block, in camera space, rather than from one Box3
    // around the lot. A row of plates laid out diagonally in world space has a
    // world-aligned bounding box far larger than anything visible in it, and
    // framing to that box leaves the floor stranded in the middle of its own
    // frame — which is exactly what it did.
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    target.traverse((node) => {
      if (!(node instanceof THREE.Mesh)) return;
      const box = node.geometry.boundingBox ?? (node.geometry.computeBoundingBox(), node.geometry.boundingBox!);
      for (let i = 0; i < 8; i++) {
        corner
          .set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z)
          .applyMatrix4(node.matrixWorld)
          .applyMatrix4(camera.matrixWorldInverse);
        minX = Math.min(minX, corner.x);
        maxX = Math.max(maxX, corner.x);
        minY = Math.min(minY, corner.y);
        maxY = Math.max(maxY, corner.y);
      }
    });
    if (minX > maxX) return;

    view = {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
      w: (maxX - minX) / pad,
      h: (maxY - minY) / pad,
    };
    bias = offset;

    // The shadow frustum is sized off the same measurement, so a wide floor
    // cannot quietly walk out of its own shadow map.
    const span = Math.max(view.w, view.h) * 0.72;
    Object.assign(key.shadow.camera, { left: -span, right: span, top: span, bottom: -span });
    key.shadow.camera.updateProjectionMatrix();
    resize();
  };

  const ticks: Tick[] = [];
  let t = 0;
  let live = false;
  let revealed = false;

  const step = (_time: number, deltaMs: number) => {
    t += Math.min(deltaMs, 100) / 1000;
    for (const fn of ticks) fn(t);
    render();
  };

  const play = () => {
    if (live || still || !ticks.length) return;
    live = true;
    gsap.ticker.add(step);
  };
  const pause = () => {
    if (!live) return;
    live = false;
    gsap.ticker.remove(step);
  };

  const ro = new ResizeObserver(resize);
  ro.observe(host);

  // Nothing renders while the canvas is off screen. The rise happens once, the
  // first time it is seen, and after that the drift owns the loop.
  const io = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return pause();
      play();
      if (revealed || still) return;
      revealed = true;
      gsap.fromTo(root.position, { y: -0.5 }, { y: 0, duration: 1.4, ease: 'power3.out', onUpdate: render });
    },
    { rootMargin: '120px' },
  );
  io.observe(host);

  resize();

  return {
    root,
    scene,
    render,
    fit,
    onTick(fn) {
      ticks.push(fn);
      if (still) {
        // Reduced motion is not "no design" — it is the same composition,
        // arrived at instantly. Draw the pose the drift settles into.
        fn(37);
        render();
      } else {
        play();
      }
    },
    dispose() {
      pause();
      ro.disconnect();
      io.disconnect();
      gsap.killTweensOf(root.position);
      scene.traverse((node) => {
        if (node instanceof THREE.Mesh) node.geometry.dispose();
      });
      probe.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      renderer.domElement.remove();
    },
  };
}
